import "@/App.css";
import { BrowserRouter, Routes, Route, useSearchParams, Link } from "react-router-dom";
import { 
  ArrowRight,
  Bell,
  Building2, 
  Calendar, 
  Check, 
  ChevronRight,
  Clock,
  CreditCard,
  DollarSign,
  FileText, 
  Loader2,
  Mail,
  MessageSquare,
  Play,
  Plus,
  RefreshCcw, 
  Smartphone,
  Sparkles,
  TrendingUp,
  Users,
  CheckCircle,
  XCircle,
  AlertCircle,
  BarChart3,
  Zap
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { useState, useEffect } from "react";
import axios from "axios";
import TermsOfService from "@/pages/TermsOfService";
import PrivacyPolicy from "@/pages/PrivacyPolicy";
import ClientChat from "@/pages/ClientChat";
import Support from "@/pages/Support";
import DemoModal from "@/components/DemoModal";
import GettingStartedSection from "@/components/GettingStarted";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Header Component
const Header = ({ onStartTrial }) => (
  <header className="fixed top-0 left-0 right-0 z-50 bg-white/80 backdrop-blur-lg border-b border-slate-100" data-testid="header">
    <div className="max-w-6xl mx-auto px-6">
      <div className="flex items-center justify-between h-16">
        <a href="/" className="flex items-center gap-2" data-testid="logo">
          <img 
            src="https://customer-assets.emergentagent.com/job_ops-portal-8/artifacts/a8dmjt8s_clientops-ios-1024.png" 
            alt="ClientOps Logo" 
            className="w-8 h-8 rounded-lg"
          />
          <span className="text-lg font-semibold text-slate-900" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
            ClientOps<sup className="text-[10px] text-slate-400 ml-0.5">™</sup>
          </span>
        </a>
        <nav className="hidden md:flex items-center gap-8">
          <a href="#features" className="text-slate-600 hover:text-slate-900 text-sm font-medium transition-colors">Features</a>
          <a href="#pricing" className="text-slate-600 hover:text-slate-900 text-sm font-medium transition-colors">Pricing</a>
          <a href="#how-it-works" className="text-slate-600 hover:text-slate-900 text-sm font-medium transition-colors">How it Works</a>
          <Link to="/support" className="text-slate-600 hover:text-slate-900 text-sm font-medium transition-colors">Support</Link>
        </nav>
        <div className="flex items-center gap-3">
          <a 
            href="https://app.clientopsapp.com/login" 
            className="hidden sm:block text-slate-600 hover:text-slate-900 text-sm font-medium transition-colors px-3 py-2"
            data-testid="chat-btn"
          >
            Chat
          </a>
          <a 
            href="https://app.clientopsapp.com/login" 
            className="hidden sm:flex items-center gap-2 text-slate-700 hover:text-slate-900 text-sm font-medium transition-colors px-3 py-2 bg-slate-100 hover:bg-slate-200 rounded-lg"
            data-testid="login-btn"
          >
            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <rect x="2" y="3" width="20" height="14" rx="2" ry="2"></rect>
              <line x1="8" y1="21" x2="16" y2="21"></line>
              <line x1="12" y1="17" x2="12" y2="21"></line>
            </svg>
            Web App Login
          </a>
          <Button 
            className="bg-[#3B82F6] text-white hover:bg-[#2563EB] text-sm font-medium px-4 h-9 rounded-lg" 
            data-testid="header-cta"
            onClick={() => onStartTrial('starter')}
          >
            Start Free Trial
          </Button>
        </div>
      </div>
    </div>
  </header>
);

// Hero Section - Clean, focused
const HeroSection = ({ onStartTrial, isLoading, onWatchDemo }) => (
  <section className="pt-28 pb-20 bg-gradient-to-b from-[#F7F8FA] to-white" data-testid="hero-section">
    <div className="max-w-6xl mx-auto px-6">
      <div className="max-w-3xl mx-auto text-center mb-16">
        <h1 className="text-4xl sm:text-5xl lg:text-6xl font-semibold text-slate-900 tracking-tight leading-[1.1] mb-6" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
          Never Lose Revenue Because You Forgot to Follow Up
        </h1>
        <p className="text-xl text-slate-500 leading-relaxed mb-8 max-w-2xl mx-auto">
          ClientOps shows consultants exactly which clients need attention so opportunities never slip through the cracks.
        </p>
        <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
          <Button 
            className="bg-[#3B82F6] text-white hover:bg-[#2563EB] h-12 px-6 text-base font-medium rounded-lg shadow-lg shadow-blue-500/25" 
            data-testid="hero-cta-primary"
            onClick={() => onStartTrial('starter')}
            disabled={isLoading}
          >
            {isLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Start Free Trial'}
          </Button>
          <Button 
            variant="outline" 
            className="border-slate-200 text-slate-700 hover:bg-slate-50 hover:border-slate-300 h-12 px-6 text-base font-medium rounded-lg" 
            data-testid="hero-cta-secondary"
            onClick={() => onWatchDemo()}
          >
            <Play className="w-4 h-4 mr-2 fill-current" />
            Watch Demo
          </Button>
        </div>
        <p className="text-sm text-slate-400 mt-4">No complicated CRM setup. Start managing your clients in minutes.</p>
      </div>
      
      {/* Product Screenshot - Revenue Radar & Follow-Up Queue */}
      <div className="max-w-5xl mx-auto">
        <div className="bg-white rounded-2xl border border-slate-200 p-6 md:p-8 shadow-xl shadow-slate-200/50">
          <div className="grid md:grid-cols-3 gap-6">
            {/* Revenue Radar Card */}
            <div className="bg-gradient-to-br from-[#6366F1]/5 to-[#6366F1]/10 rounded-xl border border-[#6366F1]/20 p-5">
              <div className="flex items-center gap-2 mb-4">
                <div className="w-8 h-8 bg-[#6366F1] rounded-lg flex items-center justify-center">
                  <Zap className="w-4 h-4 text-white" />
                </div>
                <span className="font-semibold text-slate-900 text-sm">Revenue Radar</span>
              </div>
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <span className="text-slate-500 text-sm">Clients need attention</span>
                  <span className="text-2xl font-bold text-slate-900">3</span>
                </div>
                <div className="h-px bg-[#6366F1]/20"></div>
                <div className="flex items-center justify-between">
                  <span className="text-slate-500 text-sm">Potential revenue</span>
                  <span className="text-2xl font-bold text-[#6366F1]">$2,250</span>
                </div>
              </div>
            </div>

            {/* Follow-Up Queue */}
            <div className="md:col-span-2 bg-white rounded-xl border border-slate-200 p-5 shadow-sm">
              <div className="flex items-center gap-2 mb-4">
                <div className="w-8 h-8 bg-[#22C55E] rounded-lg flex items-center justify-center">
                  <Clock className="w-4 h-4 text-white" />
                </div>
                <span className="font-semibold text-slate-900 text-sm">Follow-Up Queue</span>
              </div>
              <div className="space-y-3">
                <div className="flex items-center justify-between p-3 bg-red-50 rounded-lg border border-red-100">
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 bg-slate-200 rounded-full flex items-center justify-center text-xs font-medium text-slate-600">CG</div>
                    <span className="font-medium text-slate-900 text-sm">Corey Gomez</span>
                  </div>
                  <span className="text-xs font-medium text-red-600 bg-red-100 px-2 py-1 rounded-full">Overdue</span>
                </div>
                <div className="flex items-center justify-between p-3 bg-[#F59E0B]/10 rounded-lg border border-[#F59E0B]/20">
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 bg-slate-200 rounded-full flex items-center justify-center text-xs font-medium text-slate-600">ML</div>
                    <span className="font-medium text-slate-900 text-sm">Maria Lopez</span>
                  </div>
                  <span className="text-xs font-medium text-[#F59E0B] bg-[#F59E0B]/20 px-2 py-1 rounded-full">Due Today</span>
                </div>
                <div className="flex items-center justify-between p-3 bg-[#22C55E]/10 rounded-lg border border-[#22C55E]/20">
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 bg-slate-200 rounded-full flex items-center justify-center text-xs font-medium text-slate-600">JS</div>
                    <span className="font-medium text-slate-900 text-sm">Jose Soto</span>
                  </div>
                  <span className="text-xs font-medium text-[#22C55E] bg-[#22C55E]/20 px-2 py-1 rounded-full">Upcoming</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </section>
);

// How ClientOps Works Section - Simple 4-step workflow
const HowClientOpsWorksSection = () => (
  <section className="py-16 bg-[#F7F8FA]" data-testid="how-clientops-works-section">
    <div className="max-w-6xl mx-auto px-6">
      <div className="text-center mb-12">
        <h2 className="text-2xl sm:text-3xl font-semibold text-slate-900 tracking-tight" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
          How ClientOps Works
        </h2>
      </div>
      
      <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
        {[
          { icon: Users, title: "Add Clients", desc: "Import or create client records in seconds.", color: "bg-[#3B82F6]" },
          { icon: FileText, title: "Track Conversations", desc: "Keep notes, reminders, and interactions organized.", color: "bg-[#6366F1]" },
          { icon: Bell, title: "Follow the Queue", desc: "See exactly which clients need attention.", color: "bg-[#22C55E]" },
          { icon: DollarSign, title: "Recover Revenue", desc: "Stay on top of follow-ups and opportunities.", color: "bg-[#F59E0B]" }
        ].map((step, index) => (
          <div key={index} className="text-center">
            <div className={`w-14 h-14 ${step.color} rounded-xl flex items-center justify-center mx-auto mb-4 shadow-lg`} style={{ boxShadow: `0 8px 16px -4px ${step.color === 'bg-[#3B82F6]' ? 'rgba(59,130,246,0.3)' : step.color === 'bg-[#6366F1]' ? 'rgba(99,102,241,0.3)' : step.color === 'bg-[#22C55E]' ? 'rgba(34,197,94,0.3)' : 'rgba(245,158,11,0.3)'}` }}>
              <step.icon className="w-7 h-7 text-white" />
            </div>
            <h3 className="font-semibold text-slate-900 mb-1 text-sm">{step.title}</h3>
            <p className="text-slate-500 text-sm">{step.desc}</p>
          </div>
        ))}
      </div>

      {/* Visual workflow arrow */}
      <div className="hidden md:flex items-center justify-center gap-2 mt-8 text-slate-300">
        <span className="text-xs font-medium text-slate-400">Client Added</span>
        <ChevronRight className="w-4 h-4" />
        <span className="text-xs font-medium text-slate-400">Follow-Up Reminder</span>
        <ChevronRight className="w-4 h-4" />
        <span className="text-xs font-medium text-slate-400">Contact Client</span>
        <ChevronRight className="w-4 h-4" />
        <span className="text-xs font-medium text-slate-400">Create Invoice</span>
        <ChevronRight className="w-4 h-4" />
        <span className="text-xs font-medium text-slate-400">Get Paid</span>
      </div>
    </div>
  </section>
);

// Problem Section
const ProblemSection = () => (
  <section className="py-24 bg-white" data-testid="problem-section">
    <div className="max-w-3xl mx-auto px-6 text-center">
      <h2 className="text-3xl sm:text-4xl font-semibold text-slate-900 tracking-tight mb-6" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
        Opportunities Slip Through the Cracks
      </h2>
      <p className="text-lg text-slate-600 leading-relaxed mb-8">
        Consultants rarely lose clients because of poor service.
      </p>
      <div className="text-left max-w-lg mx-auto space-y-4 mb-8">
        <p className="text-slate-600">They lose opportunities because:</p>
        <ul className="space-y-3">
          {[
            "Follow-ups are forgotten",
            "Client conversations are scattered", 
            "Opportunities disappear before action is taken"
          ].map((item, index) => (
            <li key={index} className="flex items-start gap-3">
              <div className="w-5 h-5 bg-red-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                <XCircle className="w-3 h-3 text-red-500" />
              </div>
              <span className="text-slate-700">{item}</span>
            </li>
          ))}
        </ul>
      </div>
      <p className="text-slate-500">
        Managing dozens of relationships manually becomes overwhelming.
      </p>
      <p className="text-slate-900 font-medium mt-4">
        ClientOps solves this problem.
      </p>
    </div>
  </section>
);

// Solution Section
const SolutionSection = () => (
  <section id="features" className="py-24 bg-[#F7F8FA]" data-testid="solution-section">
    <div className="max-w-6xl mx-auto px-6">
      <div className="max-w-3xl mx-auto text-center mb-16">
        <h2 className="text-3xl sm:text-4xl font-semibold text-slate-900 tracking-tight mb-6" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
          Meet ClientOps
        </h2>
        <p className="text-lg text-slate-500 leading-relaxed">
          ClientOps acts like a daily command center for your client relationships. Instead of digging through emails and notes, you instantly see which clients need attention and where revenue opportunities exist.
        </p>
      </div>

      <div className="grid md:grid-cols-2 gap-6 lg:gap-8">
        {/* Revenue Radar */}
        <div className="bg-white rounded-2xl p-6 md:p-8 border border-[#6366F1]/20 hover:border-[#6366F1]/40 hover:shadow-lg hover:shadow-[#6366F1]/10 transition-all">
          <div className="w-12 h-12 bg-[#6366F1] rounded-xl flex items-center justify-center mb-5 shadow-lg shadow-[#6366F1]/30">
            <Zap className="w-6 h-6 text-white" />
          </div>
          <h3 className="text-xl font-semibold text-slate-900 mb-3" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
            Revenue Radar
          </h3>
          <p className="text-slate-600 mb-6">
            Identify potential revenue opportunities before they disappear.
          </p>
          <div className="bg-white rounded-xl border border-slate-200 p-4">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm text-slate-500">Clients need attention</span>
              <span className="text-xl font-bold text-slate-900">3</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm text-slate-500">Potential revenue</span>
              <span className="text-xl font-bold text-[#6366F1]">$2,250</span>
            </div>
          </div>
        </div>

        {/* Follow-Up Queue */}
        <div className="bg-white rounded-2xl p-6 md:p-8 border border-[#22C55E]/20 hover:border-[#22C55E]/40 hover:shadow-lg hover:shadow-[#22C55E]/10 transition-all">
          <div className="w-12 h-12 bg-[#22C55E] rounded-xl flex items-center justify-center mb-5 shadow-lg shadow-[#22C55E]/30">
            <Clock className="w-6 h-6 text-white" />
          </div>
          <h3 className="text-xl font-semibold text-slate-900 mb-3" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
            Follow-Up Queue
          </h3>
          <p className="text-slate-600 mb-6">
            ClientOps shows exactly who needs attention today.
          </p>
          <div className="bg-white rounded-xl border border-slate-200 p-4 space-y-2">
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium text-slate-900">Corey Gomez</span>
              <span className="text-xs text-red-600">overdue</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium text-slate-900">Maria Lopez</span>
              <span className="text-xs text-[#F59E0B]">due today</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium text-slate-900">Jose Soto</span>
              <span className="text-xs text-[#22C55E]">upcoming</span>
            </div>
          </div>
        </div>

        {/* Client Activity Timeline */}
        <div className="bg-white rounded-2xl p-6 md:p-8 border border-[#3B82F6]/20 hover:border-[#3B82F6]/40 hover:shadow-lg hover:shadow-[#3B82F6]/10 transition-all">
          <div className="w-12 h-12 bg-[#3B82F6] rounded-xl flex items-center justify-center mb-5 shadow-lg shadow-[#3B82F6]/30">
            <FileText className="w-6 h-6 text-white" />
          </div>
          <h3 className="text-xl font-semibold text-slate-900 mb-3" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
            Client Activity Timeline
          </h3>
          <p className="text-slate-600 mb-6">
            Track every client interaction in one place. Notes, reminders, invoices, and communication history stay organized.
          </p>
          <div className="bg-white rounded-xl border border-slate-200 p-4 space-y-3">
            <div className="flex items-center gap-3">
              <div className="w-2 h-2 bg-[#3B82F6] rounded-full"></div>
              <span className="text-sm text-slate-600">Invoice sent — $1,500</span>
            </div>
            <div className="flex items-center gap-3">
              <div className="w-2 h-2 bg-slate-300 rounded-full"></div>
              <span className="text-sm text-slate-600">Follow-up call completed</span>
            </div>
            <div className="flex items-center gap-3">
              <div className="w-2 h-2 bg-slate-300 rounded-full"></div>
              <span className="text-sm text-slate-600">Note added</span>
            </div>
          </div>
        </div>

        {/* Financial Overview */}
        <div className="bg-white rounded-2xl p-6 md:p-8 border border-[#F59E0B]/20 hover:border-[#F59E0B]/40 hover:shadow-lg hover:shadow-[#F59E0B]/10 transition-all">
          <div className="w-12 h-12 bg-[#F59E0B] rounded-xl flex items-center justify-center mb-5 shadow-lg shadow-[#F59E0B]/30">
            <DollarSign className="w-6 h-6 text-white" />
          </div>
          <h3 className="text-xl font-semibold text-slate-900 mb-3" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
            Financial Overview
          </h3>
          <p className="text-slate-600 mb-6">
            Track invoices, payments, and revenue at a glance without complicated accounting software.
          </p>
          <div className="bg-white rounded-xl border border-slate-200 p-4 grid grid-cols-2 gap-4">
            <div>
              <span className="text-sm text-slate-500">Revenue</span>
              <p className="text-lg font-bold text-[#22C55E]">$12,450</p>
            </div>
            <div>
              <span className="text-sm text-slate-500">Outstanding</span>
              <p className="text-lg font-bold text-[#F59E0B]">$2,100</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  </section>
);

// Product Screenshots Section
const ProductScreenshotsSection = () => (
  <section className="py-24 bg-white" data-testid="screenshots-section">
    <div className="max-w-6xl mx-auto px-6">
      <div className="max-w-3xl mx-auto text-center mb-16">
        <h2 className="text-3xl sm:text-4xl font-semibold text-slate-900 tracking-tight mb-6" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
          Your Client Command Center
        </h2>
      </div>

      <div className="grid md:grid-cols-3 gap-8">
        {/* Revenue Radar */}
        <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden hover:border-[#6366F1]/40 hover:shadow-lg hover:shadow-[#6366F1]/10 transition-all">
          <div className="p-6 bg-gradient-to-br from-[#6366F1]/5 to-[#6366F1]/10 border-b border-[#6366F1]/10">
            <div className="bg-white rounded-xl border border-slate-200 p-4">
              <div className="flex items-center gap-2 mb-3">
                <div className="w-6 h-6 bg-[#6366F1] rounded-lg flex items-center justify-center">
                  <Zap className="w-3 h-3 text-white" />
                </div>
                <span className="font-semibold text-slate-900 text-xs">Revenue Radar</span>
              </div>
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <span className="text-slate-500 text-xs">Clients need attention</span>
                  <span className="text-lg font-bold text-slate-900">3</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-slate-500 text-xs">Potential revenue</span>
                  <span className="text-lg font-bold text-[#6366F1]">$2,250</span>
                </div>
              </div>
            </div>
          </div>
          <div className="p-5">
            <h4 className="font-semibold text-slate-900 mb-1">Revenue Radar</h4>
            <p className="text-sm text-slate-500">Identify opportunities that need attention before revenue slips through the cracks.</p>
          </div>
        </div>

        {/* Follow-Up Queue */}
        <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden hover:border-[#22C55E]/40 hover:shadow-lg hover:shadow-[#22C55E]/10 transition-all">
          <div className="p-6 bg-gradient-to-br from-[#22C55E]/5 to-[#22C55E]/10 border-b border-[#22C55E]/10">
            <div className="bg-white rounded-xl border border-slate-200 p-4 space-y-2">
              <div className="flex items-center justify-between">
                <span className="text-xs font-medium text-slate-900">Corey Gomez</span>
                <span className="text-[10px] text-red-600 bg-red-50 px-2 py-0.5 rounded-full">overdue</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-xs font-medium text-slate-900">Maria Lopez</span>
                <span className="text-[10px] text-[#F59E0B] bg-[#F59E0B]/10 px-2 py-0.5 rounded-full">due today</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-xs font-medium text-slate-900">Jose Soto</span>
                <span className="text-[10px] text-[#22C55E] bg-[#22C55E]/10 px-2 py-0.5 rounded-full">upcoming</span>
              </div>
            </div>
          </div>
          <div className="p-5">
            <h4 className="font-semibold text-slate-900 mb-1">Follow-Up Queue</h4>
            <p className="text-sm text-slate-500">See exactly which clients need attention today.</p>
          </div>
        </div>

        {/* Financial Dashboard */}
        <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden hover:border-[#F59E0B]/40 hover:shadow-lg hover:shadow-[#F59E0B]/10 transition-all">
          <div className="p-6 bg-gradient-to-br from-[#F59E0B]/5 to-[#F59E0B]/10 border-b border-[#F59E0B]/10">
            <div className="bg-white rounded-xl border border-slate-200 p-4">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <span className="text-[10px] text-slate-500">Pending</span>
                  <p className="text-lg font-bold text-[#F59E0B]">$850</p>
                </div>
                <div>
                  <span className="text-[10px] text-slate-500">Collected</span>
                  <p className="text-lg font-bold text-[#22C55E]">$2,200</p>
                </div>
              </div>
            </div>
          </div>
          <div className="p-5">
            <h4 className="font-semibold text-slate-900 mb-1">Financial Dashboard</h4>
            <p className="text-sm text-slate-500">Track invoices, payments, and revenue in one place.</p>
          </div>
        </div>
      </div>
    </div>
  </section>
);

// Import LayoutDashboard for screenshots section
const LayoutDashboard = ({ className, ...props }) => (
  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className} {...props}>
    <rect x="3" y="3" width="7" height="9"></rect>
    <rect x="14" y="3" width="7" height="5"></rect>
    <rect x="14" y="12" width="7" height="9"></rect>
    <rect x="3" y="16" width="7" height="5"></rect>
  </svg>
);

// How It Works Section
const HowItWorksSection = () => (
  <section id="how-it-works" className="py-24 bg-[#F7F8FA]" data-testid="how-it-works-section">
    <div className="max-w-6xl mx-auto px-6">
      <div className="max-w-3xl mx-auto text-center mb-16">
        <h2 className="text-3xl sm:text-4xl font-semibold text-slate-900 tracking-tight mb-6" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
          How ClientOps Works
        </h2>
      </div>

      <div className="grid md:grid-cols-3 gap-8">
        {[
          {
            step: "1",
            title: "Add your clients",
            desc: "Create client records and organize them by business or category.",
            color: "bg-[#3B82F6]",
            shadow: "shadow-[#3B82F6]/30"
          },
          {
            step: "2", 
            title: "Track interactions",
            desc: "ClientOps records notes, reminders, invoices, and communication history.",
            color: "bg-[#6366F1]",
            shadow: "shadow-[#6366F1]/30"
          },
          {
            step: "3",
            title: "Follow the daily queue",
            desc: "ClientOps shows exactly who needs attention so opportunities never slip through the cracks.",
            color: "bg-[#22C55E]",
            shadow: "shadow-[#22C55E]/30"
          }
        ].map((item, index) => (
          <div key={index} className="text-center">
            <div className={`w-12 h-12 ${item.color} text-white rounded-full flex items-center justify-center mx-auto mb-5 text-xl font-semibold shadow-lg ${item.shadow}`}>
              {item.step}
            </div>
            <h3 className="text-lg font-semibold text-slate-900 mb-2" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
              {item.title}
            </h3>
            <p className="text-slate-500">
              {item.desc}
            </p>
          </div>
        ))}
      </div>
    </div>
  </section>
);

// Who It's For Section
const WhoItsForSection = () => (
  <section className="py-24 bg-white" data-testid="who-its-for-section">
    <div className="max-w-6xl mx-auto px-6">
      <div className="max-w-3xl mx-auto text-center mb-12">
        <h2 className="text-3xl sm:text-4xl font-semibold text-slate-900 tracking-tight mb-4" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
          Built for Professionals Who Manage Client Relationships
        </h2>
        <p className="text-lg text-slate-500">
          ClientOps helps professionals stay organized, follow up consistently, and recover revenue that would otherwise be lost.
        </p>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        {[
          { title: "Consultants", icon: Users, color: "#3B82F6" },
          { title: "Freelancers", icon: Sparkles, color: "#6366F1" },
          { title: "Agencies", icon: Building2, color: "#22C55E" },
          { title: "Coaches", icon: MessageSquare, color: "#F59E0B" },
          { title: "Service Businesses", icon: TrendingUp, color: "#3B82F6" }
        ].map((item, index) => (
          <div key={index} className="bg-white border border-slate-200 rounded-xl p-5 text-center hover:shadow-md hover:border-slate-300 transition-all">
            <div className="w-10 h-10 rounded-lg flex items-center justify-center mx-auto mb-3" style={{ backgroundColor: `${item.color}10` }}>
              <item.icon className="w-5 h-5" style={{ color: item.color }} />
            </div>
            <span className="text-sm font-medium text-slate-700">{item.title}</span>
          </div>
        ))}
      </div>
    </div>
  </section>
);

// Pricing Section
const PricingSection = ({ onStartTrial, isLoading }) => (
  <section id="pricing" className="py-24 bg-[#F7F8FA]" data-testid="pricing-section">
    <div className="max-w-5xl mx-auto px-6">
      <div className="max-w-3xl mx-auto text-center mb-16">
        <h2 className="text-3xl sm:text-4xl font-semibold text-slate-900 tracking-tight mb-6" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
          Simple, Transparent Pricing
        </h2>
        <p className="text-lg text-slate-500">
          For consultants who want to stay organized and recover more revenue.
        </p>
      </div>
      
      <div className="grid md:grid-cols-2 gap-6 lg:gap-8">
        {/* Starter Plan */}
        <div className="bg-white border border-slate-200 rounded-2xl p-6 md:p-8 shadow-sm" data-testid="pricing-starter">
          <div className="mb-6">
            <h3 className="text-lg font-semibold text-slate-900 mb-2" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>Starter</h3>
            <div className="flex items-baseline gap-1">
              <span className="text-4xl font-bold text-slate-900">$29</span>
              <span className="text-slate-500">/month</span>
            </div>
            <p className="text-sm text-slate-500 mt-1">or $290/year (save 17%)</p>
          </div>
          <p className="text-sm text-slate-600 mb-6">
            Best for independent consultants who want a simple system to manage client relationships and follow-ups.
          </p>
          <ul className="space-y-3 mb-8">
            {[
              "Up to 250 clients",
              "Up to 2 businesses/categories",
              "Client tracking and management",
              "Follow-up reminders with push notifications",
              "Visual dashboard",
              "Custom client statuses",
              "Manual payment tracking",
              "Basic reporting"
            ].map((feature, index) => (
              <li key={index} className="flex items-center gap-3 text-sm text-slate-600">
                <Check className="w-4 h-4 text-[#3B82F6] flex-shrink-0" strokeWidth={2} />
                <span>{feature}</span>
              </li>
            ))}
          </ul>
          <Button 
            variant="outline" 
            className="w-full h-11 border-[#3B82F6] text-[#3B82F6] hover:bg-[#3B82F6]/5 font-medium rounded-lg" 
            data-testid="pricing-starter-cta"
            onClick={() => onStartTrial('starter')}
            disabled={isLoading}
          >
            Start with Starter
          </Button>
        </div>

        {/* Pro Plan */}
        <div className="bg-slate-900 text-white rounded-2xl p-6 md:p-8 relative shadow-xl shadow-slate-900/20" data-testid="pricing-pro">
          <div className="absolute -top-3 left-6">
            <span className="bg-[#3B82F6] text-white text-xs font-bold px-3 py-1 rounded-full shadow-lg shadow-[#3B82F6]/30">
              Most Popular
            </span>
          </div>
          <div className="mb-6">
            <h3 className="text-lg font-semibold mb-2" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>Pro</h3>
            <div className="flex items-baseline gap-1">
              <span className="text-4xl font-bold">$59</span>
              <span className="text-slate-400">/month</span>
            </div>
            <p className="text-sm text-slate-400 mt-1">or $590/year (save 17%)</p>
          </div>
          <p className="text-sm text-slate-300 mb-6">
            Built for consultants who want to manage relationships, invoices, and revenue in one place.
          </p>
          <p className="text-xs text-slate-400 mb-3 font-medium">Everything in Starter, plus:</p>
          <ul className="space-y-3 mb-8">
            {[
              "Unlimited clients",
              "Unlimited businesses/categories",
              "Professional invoice generation",
              "Stripe payment integration",
              "Bulk import/export (CSV, Excel, PDF)",
              "Advanced filtering and search",
              "AI Assistant for insights",
              "Real-time messaging with clients",
              "Revenue tracking dashboard",
              "Priority support"
            ].map((feature, index) => (
              <li key={index} className="flex items-center gap-3 text-sm text-slate-300">
                <Check className="w-4 h-4 text-[#3B82F6] flex-shrink-0" strokeWidth={2} />
                <span>{feature}</span>
              </li>
            ))}
          </ul>
          <Button 
            className="w-full h-11 bg-white text-slate-900 hover:bg-slate-100 font-medium rounded-lg" 
            data-testid="pricing-pro-cta"
            onClick={() => onStartTrial('pro')}
            disabled={isLoading}
          >
            Start Pro Trial
          </Button>
        </div>
      </div>
    </div>
  </section>
);

// Testimonials Placeholder
const TestimonialsSection = () => (
  <section className="py-24 bg-white" data-testid="testimonials-section">
    <div className="max-w-5xl mx-auto px-6">
      <div className="text-center mb-12">
        <h2 className="text-3xl sm:text-4xl font-semibold text-slate-900 tracking-tight mb-4" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
          Trusted by Independent Professionals
        </h2>
        <p className="text-lg text-slate-500">See how ClientOps helps professionals stay organized and grow revenue.</p>
      </div>
      <div className="grid md:grid-cols-3 gap-6">
        {[
          {
            quote: "I was losing track of clients and leaving money on the table. ClientOps showed me exactly who needed follow-up and helped me recover over $4,000 in the first month alone.",
            name: "Marcus Rivera",
            role: "Insurance Agent",
            initials: "MR",
            color: "#3B82F6",
          },
          {
            quote: "Before ClientOps, I had sticky notes everywhere and no system. Now my entire client pipeline, invoicing, and follow-ups live in one place. It's the CRM that actually fits how I work.",
            name: "Priya Sharma",
            role: "Freelance Consultant",
            initials: "PS",
            color: "#6366F1",
          },
          {
            quote: "The follow-up queue is a game changer. I used to forget about clients for weeks. Now I open ClientOps each morning and know exactly who to call. My retention rate is up 30%.",
            name: "David Chen",
            role: "Real Estate Advisor",
            initials: "DC",
            color: "#22C55E",
          },
        ].map((t, i) => (
          <div key={i} className="bg-[#F7F8FA] rounded-2xl border border-slate-200 p-6 flex flex-col" data-testid={`testimonial-${i}`}>
            <div className="flex gap-1 mb-4">
              {[1,2,3,4,5].map(s => (
                <svg key={s} className="w-4 h-4 text-[#F59E0B] fill-[#F59E0B]" viewBox="0 0 24 24"><path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/></svg>
              ))}
            </div>
            <p className="text-slate-600 text-sm leading-relaxed flex-1 mb-5">"{t.quote}"</p>
            <div className="flex items-center gap-3 pt-4 border-t border-slate-200">
              <div className="w-9 h-9 rounded-full flex items-center justify-center text-xs font-bold text-white" style={{ backgroundColor: t.color }}>
                {t.initials}
              </div>
              <div>
                <p className="font-semibold text-slate-900 text-sm">{t.name}</p>
                <p className="text-slate-400 text-xs">{t.role}</p>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  </section>
);

// Multi-Platform Section
const MultiPlatformSection = () => (
  <section className="py-20 bg-[#F7F8FA]" data-testid="multi-platform-section">
    <div className="max-w-6xl mx-auto px-6">
      <div className="text-center mb-12">
        <h3 className="text-2xl font-semibold text-slate-900 mb-3" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
          Work from anywhere
        </h3>
        <p className="text-slate-500">
          Access ClientOps on iOS, Web, and Android (coming soon). Everything syncs instantly.
        </p>
      </div>
      <div className="flex flex-wrap items-center justify-center gap-6">
        <a href="https://apps.apple.com/app/clientops" target="_blank" rel="noopener noreferrer" className="transition-transform hover:scale-105">
          <img 
            src="https://developer.apple.com/assets/elements/badges/download-on-the-app-store.svg" 
            alt="Download on the App Store" 
            className="h-11"
          />
        </a>
        <a 
          href="https://app.clientopsapp.com/login" 
          className="flex items-center gap-2 px-5 py-2.5 bg-slate-900 text-white rounded-lg font-medium text-sm hover:bg-slate-800 transition-colors"
        >
          <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <rect x="2" y="3" width="20" height="14" rx="2" ry="2"></rect>
            <line x1="8" y1="21" x2="16" y2="21"></line>
            <line x1="12" y1="17" x2="12" y2="21"></line>
          </svg>
          Open Web App
        </a>
        <div className="relative">
          <img 
            src="https://play.google.com/intl/en_us/badges/static/images/badges/en_badge_web_generic.png" 
            alt="Get it on Google Play" 
            className="h-16 opacity-40 grayscale"
          />
          <span className="absolute -top-1 -right-1 bg-amber-400 text-slate-900 text-[10px] font-bold px-2 py-0.5 rounded-full">
            SOON
          </span>
        </div>
      </div>
    </div>
  </section>
);

// Final CTA Section
const FinalCTASection = ({ onStartTrial, isLoading }) => (
  <section className="py-24 bg-slate-900" data-testid="final-cta-section">
    <div className="max-w-3xl mx-auto px-6 text-center">
      <h2 className="text-3xl sm:text-4xl font-semibold text-white tracking-tight mb-6" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
        Stop Losing Opportunities
      </h2>
      <p className="text-lg text-slate-400 mb-8">
        ClientOps helps consultants track relationships, recover missed opportunities, and grow revenue.
      </p>
      <Button 
        className="bg-[#3B82F6] text-white hover:bg-[#2563EB] h-12 px-8 text-base font-medium rounded-lg shadow-lg shadow-[#3B82F6]/30" 
        data-testid="final-cta-btn"
        onClick={() => onStartTrial('starter')}
        disabled={isLoading}
      >
        Start Free Trial
      </Button>
      <p className="text-sm text-slate-500 mt-4">No complicated CRM setup. Start managing your clients in minutes.</p>
    </div>
  </section>
);

// Footer
const Footer = () => (
  <footer className="py-12 bg-slate-900 border-t border-slate-800" data-testid="footer">
    <div className="max-w-6xl mx-auto px-6">
      <div className="flex flex-col md:flex-row items-center justify-between gap-6">
        <div className="flex items-center gap-2">
          <img 
            src="https://customer-assets.emergentagent.com/job_ops-portal-8/artifacts/a8dmjt8s_clientops-ios-1024.png" 
            alt="ClientOps Logo" 
            className="w-6 h-6 rounded"
          />
          <span className="text-white font-semibold">ClientOps<sup className="text-[8px] text-slate-400 ml-0.5">™</sup></span>
        </div>
        <nav className="flex flex-wrap items-center justify-center gap-6">
          <a href="#pricing" className="text-slate-400 hover:text-white text-sm transition-colors">Pricing</a>
          <a href="#features" className="text-slate-400 hover:text-white text-sm transition-colors">Features</a>
          <Link to="/support" className="text-slate-400 hover:text-white text-sm transition-colors">Support</Link>
          <Link to="/privacy" className="text-slate-400 hover:text-white text-sm transition-colors">Privacy Policy</Link>
          <Link to="/terms" className="text-slate-400 hover:text-white text-sm transition-colors">Terms of Service</Link>
          <a href="mailto:hello@clientopsapp.com" className="text-slate-400 hover:text-white text-sm transition-colors">Contact</a>
          <a href="https://app.clientopsapp.com/login" className="text-slate-400 hover:text-white text-sm transition-colors">Login</a>
        </nav>
      </div>
      <div className="mt-8 pt-8 border-t border-slate-800 flex flex-col sm:flex-row items-center justify-between gap-4">
        <p className="text-slate-500 text-sm">ClientOps is a product of Noble Strategies Consulting, LLC.</p>
        <p className="text-slate-500 text-sm">© 2026 Noble Strategies Consulting, LLC. All rights reserved.</p>
        <div className="flex items-center gap-4" data-testid="social-links">
          <a href="https://x.com/clientopsapp" target="_blank" rel="noopener noreferrer" className="text-slate-500 hover:text-white transition-colors" data-testid="social-x" aria-label="Follow us on X">
            <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor"><path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/></svg>
          </a>
          <a href="https://instagram.com/clientopsapp" target="_blank" rel="noopener noreferrer" className="text-slate-500 hover:text-white transition-colors" data-testid="social-instagram" aria-label="Follow us on Instagram">
            <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="2" y="2" width="20" height="20" rx="5" ry="5"/><path d="M16 11.37A4 4 0 1 1 12.63 8 4 4 0 0 1 16 11.37z"/><line x1="17.5" y1="6.5" x2="17.51" y2="6.5"/></svg>
          </a>
          <a href="https://facebook.com/clientopsapp" target="_blank" rel="noopener noreferrer" className="text-slate-500 hover:text-white transition-colors" data-testid="social-facebook" aria-label="Follow us on Facebook">
            <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor"><path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/></svg>
          </a>
        </div>
      </div>
    </div>
  </footer>
);

// Payment Status Modal
const PaymentStatusModal = ({ status, onClose }) => {
  const isSuccess = status === 'success';
  
  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/50 backdrop-blur-sm" data-testid="payment-status-modal">
      <div className="bg-white rounded-2xl p-8 max-w-md w-full mx-4 shadow-2xl text-center">
        {isSuccess ? (
          <>
            <div className="w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <CheckCircle className="w-8 h-8 text-emerald-600" />
            </div>
            <h3 className="text-2xl font-bold text-slate-900 mb-2" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
              Payment Successful!
            </h3>
            <p className="text-slate-600 mb-6">
              Thank you for subscribing to ClientOps.
            </p>
          </>
        ) : (
          <>
            <div className="w-16 h-16 bg-slate-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <XCircle className="w-8 h-8 text-slate-500" />
            </div>
            <h3 className="text-2xl font-bold text-slate-900 mb-2" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
              Payment Cancelled
            </h3>
            <p className="text-slate-600 mb-6">
              No worries! You can start your free trial anytime.
            </p>
          </>
        )}
        <Button 
          className="w-full h-11 bg-slate-900 text-white hover:bg-slate-800 font-medium rounded-lg"
          onClick={onClose}
        >
          {isSuccess ? 'Get Started' : 'Back to Home'}
        </Button>
      </div>
    </div>
  );
};

// Main Home Component
const Home = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [paymentStatus, setPaymentStatus] = useState(null);
  const [showDemo, setShowDemo] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();

  useEffect(() => {
    const sessionId = searchParams.get('session_id');
    const status = searchParams.get('status');
    
    if (sessionId && status === 'success') {
      pollPaymentStatus(sessionId);
    } else if (status === 'cancelled') {
      setPaymentStatus('cancelled');
      setSearchParams({});
    }
  }, [searchParams, setSearchParams]);

  const pollPaymentStatus = async (sessionId, attempts = 0) => {
    const maxAttempts = 5;
    if (attempts >= maxAttempts) {
      setPaymentStatus('success');
      setSearchParams({});
      return;
    }
    try {
      const response = await axios.get(`${API}/checkout/status/${sessionId}`);
      if (response.data.payment_status === 'paid') {
        setPaymentStatus('success');
        setSearchParams({});
      } else {
        setTimeout(() => pollPaymentStatus(sessionId, attempts + 1), 2000);
      }
    } catch (error) {
      setPaymentStatus('success');
      setSearchParams({});
    }
  };

  const handleStartTrial = (packageId) => {
    // The ClientOps application creates the 14-day trial after registration.
    // Keep marketing visitors in the authenticated app's billing flow,
    // rather than calling the obsolete Emergent checkout endpoint.
    const plan = packageId === 'pro' ? 'pro' : 'starter';
    window.location.assign(`https://app.clientopsapp.com/register?plan=${plan}`);
  };

  return (
    <div className="min-h-screen bg-white">
      <Header onStartTrial={handleStartTrial} />
      {paymentStatus && (
        <PaymentStatusModal status={paymentStatus} onClose={() => setPaymentStatus(null)} />
      )}
      <DemoModal open={showDemo} onClose={() => setShowDemo(false)} onStartTrial={handleStartTrial} />
      <main>
        <HeroSection onStartTrial={handleStartTrial} isLoading={isLoading} onWatchDemo={() => setShowDemo(true)} />
        <HowClientOpsWorksSection />
        <ProblemSection />
        <SolutionSection />
        <ProductScreenshotsSection />
        <HowItWorksSection />
        <WhoItsForSection />
        <PricingSection onStartTrial={handleStartTrial} isLoading={isLoading} />
        <TestimonialsSection />
        <GettingStartedSection />
        <MultiPlatformSection />
        <FinalCTASection onStartTrial={handleStartTrial} isLoading={isLoading} />
      </main>
      <Footer />
    </div>
  );
};

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <Routes>
          <Route path="/" element={<Home />} />
          <Route path="/terms" element={<TermsOfService />} />
          <Route path="/privacy" element={<PrivacyPolicy />} />
          <Route path="/support" element={<Support />} />
          <Route path="/chat/:chatId" element={<ClientChat />} />
        </Routes>
      </BrowserRouter>
    </div>
  );
}

export default App;
